import { useState } from "react";
import { Alan, Buton, cn } from "@koclukpanel/ui";
import type { KendiKodlamam } from "@koclukpanel/contracts";
import type { DenemeOzeti, KodlamaSonucu } from "./api";

const SECENEKLER = ["A", "B", "C", "D", "E"] as const;

export interface DenemeKodlamaProps {
  baslangic: KendiKodlamam;
  deneme: DenemeOzeti;
  onGonder: (cevaplar: Record<string, string>, sureDakika: number | null) => void;
  onKapat: () => void;
  gonderiliyor?: boolean;
  hata?: string | null;
  sonuc?: KodlamaSonucu | null;
}

/**
 * DENEME KODLAMA — F-EXM-04
 *
 * ================================================================
 * KAGIT OPTIK FORMUN EKRANDAKI KARSILIGI
 * ================================================================
 * Deneme kagit uzerinde cozuluyor (F-EXM-13); ogrenci burada yalnizca
 * isaretlediklerini aktariyor. Bu yuzden ekran soru soru ilerleyen bir
 * sinav arayuzu DEGIL, hepsi bir arada gorunen bir form: ogrenci
 * kagidina bakarak sirayla giriyor ve yanlis girdigini fark edince
 * geri donebiliyor.
 *
 * ================================================================
 * BOS BIRAKMAK BIR CEVAPTIR
 * ================================================================
 * Isaretlenmemis soru "cevaplanmadi" demek ve bu YANLIS'tan farkli
 * (net hesabinda goturmuyor). Secili secenege tekrar dokunmak secimi
 * KALDIRIYOR — yanlislikla isaretleyen ogrenci bos birakmaya
 * donebilmeli, aksi halde kendine ceza yazardi.
 */
export function DenemeKodlama({
  baslangic,
  deneme,
  onGonder,
  onKapat,
  gonderiliyor,
  hata,
  sonuc,
}: DenemeKodlamaProps) {
  const [cevaplar, setCevaplar] = useState<Record<string, string>>(() =>
    baslangic.durum === "HAM_CEVAPLI" ? baslangic.cevaplar : {},
  );
  const [sure, setSure] = useState(() =>
    baslangic.durum === "HAM_CEVAPLI" && baslangic.sureDakika !== null
      ? String(baslangic.sureDakika)
      : "",
  );

  const toplamSoru = deneme.bolumler.reduce((t, b) => t + b.questionCount, 0);
  const isaretli = Object.keys(cevaplar).length;

  if (sonuc) {
    return <KodlamaSonucuGorunumu sonuc={sonuc} deneme={deneme} onKapat={onKapat} />;
  }

  // F-EXM-04/05 — net girişi ham cevap içermez; boş form sonucu sessizce silmemeli.
  if (baslangic.durum === "YALNIZ_NET") {
    return (
      <div>
        <p role="status">
          Bu sonuç yalnızca net olarak girilmiştir. Cevaplarınızı düzenlemek için koçunuzla
          görüşünüz.
        </p>
        <Buton onClick={onKapat}>Kapat</Buton>
      </div>
    );
  }

  // Bolum bolum soru numaralari: bir sonraki bolum oncekinin bittigi
  // yerden devam ediyor — kagitta da oyle.
  let sayac = 0;
  const bolumler = deneme.bolumler.map((b) => {
    const bas = sayac + 1;
    sayac += b.questionCount;
    return { ...b, bas, bit: sayac };
  });

  return (
    <div className="flex flex-col gap-4 pb-24">
      <header className="flex items-start justify-between gap-3">
        <div>
          <h2 className="text-notr-900 text-lg font-semibold">{deneme.name}</h2>
          <p className="text-notr-600 text-sm">
            {isaretli} / {toplamSoru} soru işaretlendi
          </p>
        </div>
        <Buton gorunum="sessiz" onClick={onKapat}>
          Kapat
        </Buton>
      </header>

      <p className="rounded-alan bg-notr-50 text-notr-600 px-3 py-2 text-sm">
        Kâğıdındaki işaretlerini buraya aktar. Boş bıraktığın soruya dokunma — boş bırakmak yanlış
        sayılmaz.
      </p>

      {bolumler.map((b) => (
        <section key={b.id} className="flex flex-col gap-2">
          <h3 className="text-notr-900 text-sm font-semibold">
            {b.name}{" "}
            <span className="text-notr-600 font-normal">
              ({b.bas}–{b.bit})
            </span>
          </h3>

          <ul className="flex flex-col gap-1.5">
            {Array.from({ length: b.questionCount }, (_, i) => b.bas + i).map((no) => (
              <li key={no} className="flex items-center gap-2">
                <span className="text-notr-600 w-8 shrink-0 text-right text-sm tabular-nums">
                  {no}
                </span>
                <div role="radiogroup" aria-label={`${no}. soru`} className="flex flex-1 gap-1.5">
                  {SECENEKLER.map((s) => {
                    const secili = cevaplar[String(no)] === s;
                    return (
                      <button
                        key={s}
                        type="button"
                        role="radio"
                        aria-checked={secili}
                        aria-label={`${no}. soru ${s}`}
                        onClick={() =>
                          setCevaplar((mevcut) => {
                            const yeni = { ...mevcut };
                            // Ayni secenege tekrar dokunmak secimi KALDIRIYOR.
                            if (yeni[String(no)] === s) delete yeni[String(no)];
                            else yeni[String(no)] = s;
                            return yeni;
                          })
                        }
                        className={cn(
                          "min-h-dokunma rounded-alan flex-1 text-sm font-medium",
                          "ring-1 transition-colors ring-inset",
                          secili
                            ? "bg-marka-600 text-notr-0 ring-marka-600"
                            : "bg-notr-0 text-notr-700 ring-notr-300 hover:bg-notr-50",
                        )}
                      >
                        {s}
                      </button>
                    );
                  })}
                </div>
              </li>
            ))}
          </ul>
        </section>
      ))}

      <Alan
        etiket="Kaç dakikada çözdün? (isteğe bağlı)"
        type="number"
        inputMode="numeric"
        min={0}
        max={1440}
        value={sure}
        onChange={(e) => setSure(e.target.value)}
      />

      {hata && (
        <p role="alert" className="rounded-alan bg-hata-500/10 text-hata-700 px-3 py-2 text-sm">
          {hata}
        </p>
      )}

      {/*
        Gonderim BOS FORMDA da acik: hicbir soruyu isaretlemeden
        gondermek "hepsini bos biraktim" demek ve bu gecerli bir sonuc.
        Kilitleseydik, denemeye girip hicbir sey yapamayan ogrenci
        durumunu kaydedemezdi.
      */}
      <div className="border-notr-200 bg-notr-0 fixed inset-x-0 bottom-0 border-t p-3">
        <Buton
          boyut="genis"
          yukleniyor={gonderiliyor}
          onClick={() => onGonder(cevaplar, sure.trim() === "" ? null : Number(sure))}
        >
          Gönder ({isaretli} işaretli)
        </Buton>
      </div>
    </div>
  );
}

/**
 * Gonderim sonrasi ozet.
 *
 * Cevap anahtari BURADA GOSTERILMIYOR (F-EXM-12): pencere kapanmadan
 * anahtar acilmiyor ve ogrenci hangi soruyu yanlis yaptigini bu
 * ekrandan ogrenemiyor. Yalnizca sayilar veriliyor.
 */
function KodlamaSonucuGorunumu({
  sonuc,
  deneme,
  onKapat,
}: {
  sonuc: KodlamaSonucu;
  deneme: DenemeOzeti;
  onKapat: () => void;
}) {
  return (
    <div className="flex flex-col gap-4">
      <div>
        <h2 className="text-notr-900 text-lg font-semibold">{deneme.name}</h2>
        <p className="text-notr-600 mt-0.5 text-sm">
          {new Date(deneme.examDate).toLocaleDateString("tr-TR")} · cevapların kaydedildi
        </p>
      </div>

      {/*
        ================================================================
        TOPLAM NET KAHRAMAN — DÖRT KUTUDAN BİRİ DEĞİL
        ================================================================
        Önceki hâl doğru · yanlış · boş · net'i eşit dört kutuya
        koyuyordu. Çizim (`DenemeSonuc.dc.html`) neti sayfanın en
        büyük ögesi yapıyor ve sebebi öğrencinin davranışı: kodlamayı
        bitiren biri tek bir sayıya bakıyor, "kaç net". Doğru ve yanlış
        o sayının nasıl oluştuğu — açıklama, cevap değil.

        Çizimdeki rakam makarası (kayan şerit) YAZILMADI: hareket
        `prefers-reduced-motion` ve ölçüm gerektiriyor, ikisi de canlı
        doğrulama turuna kalıyor. Hiyerarşi hareket olmadan da doğru.

        Net EKRANDA yuvarlanıyor, KAYITTA değil: 39.75 ile 39.8 farklı
        öğrenciler ve sıralamada eşitlenmemeliler.
      */}
      <div className="rounded-kart border-notr-200 bg-notr-0 shadow-kart flex flex-col items-center border px-4 py-5">
        <span className="text-notr-600 text-[13px] font-medium">Toplam net</span>
        <span className="text-notr-900 text-[52px] leading-none font-semibold tabular-nums">
          {Math.round(sonuc.toplam.net * 100) / 100}
        </span>
      </div>

      <dl className="grid grid-cols-3 gap-3">
        <Kutu etiket="Doğru" deger={sonuc.toplam.dogru} />
        <Kutu etiket="Yanlış" deger={sonuc.toplam.yanlis} />
        <Kutu etiket="Boş" deger={sonuc.toplam.bos} />
      </dl>

      {sonuc.sureSupheli && (
        <p role="status" className="rounded-alan bg-uyari-500/15 text-uyari-600 px-3 py-2 text-sm">
          Girdiğin süre alışılmadık görünüyor. Yanlışsa uzmanına söyle.
        </p>
      )}

      <p className="text-notr-600 text-sm">Cevap anahtarı, deneme süresi dolduğunda açılır.</p>

      <Buton gorunum="ikincil" boyut="genis" onClick={onKapat}>
        Tamam
      </Buton>
    </div>
  );
}

function Kutu({ etiket, deger }: { etiket: string; deger: number }) {
  return (
    <div className="rounded-alan bg-notr-50 px-3 py-2">
      <dt className="text-notr-600 text-xs">{etiket}</dt>
      <dd className="text-notr-900 text-xl font-semibold tabular-nums">{deger}</dd>
    </div>
  );
}
